/**
 * Runtime environment variable accessor.
 *
 * In production containers, values come from window.__ENV__ (injected at startup
 * via env-config.js, generated by docker/start.sh).
 *
 * In local dev (Vite dev server), window.__ENV__ is empty and import.meta.env
 * has the values from .env — this path only runs when import.meta.env.DEV is true.
 *
 * Throws if the variable is missing or empty — every env var must be explicitly set.
 */
export function requireEnv(key) {
  // Runtime injection (production containers)
  if (typeof window !== 'undefined' && window.__ENV__?.[key] !== undefined && window.__ENV__[key] !== '') {
    return window.__ENV__[key];
  }
  // Local dev only — Vite dev server reads from .env files into import.meta.env.
  // This path is compile-time eliminated in production builds (import.meta.env.DEV === false).
  if (import.meta.env.DEV && import.meta.env[key] !== undefined && import.meta.env[key] !== '') {
    return import.meta.env[key];
  }
  throw new Error(
    `Required environment variable ${key} is not set. ` +
    `Check window.__ENV__ (production) or .env files (development).`
  );
}

/**
 * Environment name ('local' | 'staging' | 'production'). Stamped into
 * the login form's `section-<env>` autocomplete tokens so password
 * managers don't collide credentials across envs that share
 * gearghost.co. Falls back to 'local' when unset so a fresh `.env`
 * doesn't break local dev — staging and prod always have it from
 * deployInstance.sh.
 */
export function getEnvName() {
  if (typeof window !== 'undefined' && window.__ENV__?.VITE_ENV_NAME) {
    return window.__ENV__.VITE_ENV_NAME;
  }
  if (import.meta.env?.VITE_ENV_NAME) {
    return import.meta.env.VITE_ENV_NAME;
  }
  return 'local';
}

/**
 * Whether this build stands behind the staging gate
 * (docs/operations.md, Staging gate): the app shows a production-admin sign-in
 * before its router until the staging backend says the gate is open.
 * Keyed off VITE_ENV_NAME, which staging always has from deployInstance.sh.
 */
export function isStagingGateEnabled() {
  return getEnvName() === 'staging';
}
